'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
// Prefetched on intent, not on sight (components/IntentLink.tsx).
import Link from '@/components/IntentLink';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getOptimizedImageUrl, getPresetSvgDataUri } from '@/lib/image-url';
import { CARD_PHOTO_LIMIT } from '@/lib/listing-card';

// The photos on a listing card: a swipe on a phone, arrows under a mouse, a
// "2 / 12" counter, and no more than five photos - the rest wait on the
// listing page, which the last one points to.
//
// Each photo is a link to the listing, so a tap opens it and a sideways swipe
// scrolls the photos: the browser tells the two apart. The track leaves
// vertical panning alone (no touch-action), so a thumb that starts on a photo
// still scrolls the page.

function Photo({ src, alt, fallback, priority }: { src: string; alt: string; fallback: string; priority: boolean }) {
  const [current, setCurrent] = useState(src);
  return (
    <Image
      src={current}
      alt={alt}
      fill
      priority={priority}
      draggable={false}
      // K-14: a card is a thumbnail; 60 keeps it inside its weight budget.
      quality={60}
      // The grid: one column under 600px, two to 1023, three to 1439, then four.
      sizes="(max-width: 599px) 100vw, (max-width: 1023px) 50vw, (max-width: 1439px) 33vw, 320px"
      className="object-cover select-none transition-transform duration-500 motion-safe:pointer-fine:group-hover:scale-105"
      onError={() => setCurrent(fallback)}
    />
  );
}

export default function CardPhotoCarousel({
  images,
  total,
  title,
  href,
  lang,
  eager = false,
}: {
  images: string[];
  /** How many photos the listing has, when only the first few were sent. */
  total?: number;
  title: string;
  href: string;
  lang: string;
  /** Above the fold: the first photo loads at once and at high priority. */
  eager?: boolean;
}) {
  const isFr = lang === 'fr';
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const shown = images.slice(0, CARD_PHOTO_LIMIT);
  const count = Math.max(total ?? 0, images.length);
  const more = count - shown.length;

  const onScroll = () => {
    const el = track.current;
    if (!el || !el.clientWidth) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    if (next !== index) setIndex(next);
  };

  const go = (step: number) => {
    const el = track.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left: (index + step) * el.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
  };

  const arrow =
    'hidden pointer-fine:flex absolute top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white/90 text-neutral-900 shadow-md cursor-pointer opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity';

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* The photo strip scrolls, so it is itself a keyboard stop (a scrolling region nobody can reach by keyboard is a
          WCAG failure), with a name, and the arrow keys move it. The two arrow buttons stay for the mouse and are not
          stops of their own: the strip is the one stop a keyboard user needs. */}
      <div
        ref={track}
        onScroll={onScroll}
        tabIndex={shown.length > 1 ? 0 : undefined}
        role={shown.length > 1 ? 'group' : undefined}
        aria-roledescription={shown.length > 1 ? 'carousel' : undefined}
        aria-label={shown.length > 1 ? (isFr ? `Photos : ${title}` : `Photos: ${title}`) : undefined}
        onKeyDown={(e) => {
          if (shown.length < 2) return;
          if (e.key === 'ArrowRight' && index < shown.length - 1) { e.preventDefault(); go(1); }
          else if (e.key === 'ArrowLeft' && index > 0) { e.preventDefault(); go(-1); }
        }}
        className="flex h-full overflow-x-auto snap-x snap-mandatory overscroll-x-contain scrollbar-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-champagne"
      >
        {shown.map((url, idx) => (
          <Link
            key={idx}
            href={href}
            // The card's heading link is the one keyboards and screen readers
            // use; these are for a tap on the photo.
            tabIndex={-1}
            aria-hidden="true"
            draggable={false}
            className="relative h-full min-w-full snap-start snap-always [-webkit-touch-callout:none]"
          >
            <Photo
              src={getOptimizedImageUrl(url)}
              alt={idx === 0 ? title : ''}
              fallback={getPresetSvgDataUri(url)}
              priority={eager && idx === 0}
            />
            {idx === shown.length - 1 && more > 0 && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-base font-semibold">
                {`+${more} photos`}
              </span>
            )}
          </Link>
        ))}
      </div>

      {shown.length > 1 && index > 0 && (
        <button type="button" tabIndex={-1} onClick={() => go(-1)} aria-label={isFr ? 'Photo précédente' : 'Previous photo'} className={`${arrow} left-2`}>
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {shown.length > 1 && index < shown.length - 1 && (
        <button type="button" tabIndex={-1} onClick={() => go(1)} aria-label={isFr ? 'Photo suivante' : 'Next photo'} className={`${arrow} right-2`}>
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {count > 1 && (
        <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 z-10 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs font-medium tabular-nums">
          {index + 1} / {count}
        </span>
      )}
    </div>
  );
}
